import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';

import type { SnippetsPlugin } from '../snippetsPluginType';

import {
	snippetsPluginKey,
	SNIPPETS_META_ADD_LOADER,
	SNIPPETS_META_REMOVE_LOADER,
	type PendingSnippetLoader,
	type SnippetsMeta,
} from './main';

type SnippetsApi = Parameters<SnippetsPlugin>[0]['api'];

interface AddLoaderParams {
	api: SnippetsApi;
	dismissTr: ReturnType<
		import('@atlaskit/editor-common/provider-factory').QuickInsertItem['action']
	>;
	loaderId: string;
}

interface RemoveLoaderParams {
	api: SnippetsApi;
	loaderId: string;
}

interface RemoveLoaderAndInsertParams {
	adf: ADFEntity[];
	api: SnippetsApi;
	loaderId: string;
	loaderPosAtAdd: number | undefined;
}

const isTransactionWithMeta = (value: unknown): value is EditorState['tr'] =>
	value != null && typeof value === 'object' && 'selection' in value && 'setMeta' in value;

export const addSnippetLoader = ({
	api,
	dismissTr,
	loaderId,
}: AddLoaderParams): number | undefined => {
	let loaderPosAtAdd: number | undefined;

	if (isTransactionWithMeta(dismissTr)) {
		const pos = dismissTr.selection.from;
		loaderPosAtAdd = pos;
		dismissTr.setMeta(snippetsPluginKey, {
			type: SNIPPETS_META_ADD_LOADER,
			loader: { id: loaderId, pos },
		} satisfies SnippetsMeta);
		return loaderPosAtAdd;
	}

	api?.core?.actions.execute(({ tr }: { tr: Transaction }) => {
		const pos = tr.selection.from;
		loaderPosAtAdd = pos;
		tr.setMeta(snippetsPluginKey, {
			type: SNIPPETS_META_ADD_LOADER,
			loader: { id: loaderId, pos },
		} satisfies SnippetsMeta);
		return tr;
	});

	return loaderPosAtAdd;
};

export const removeSnippetLoader = ({ api, loaderId }: RemoveLoaderParams): void => {
	api?.core?.actions.execute(({ tr }: { tr: Transaction }) =>
		tr.setMeta(snippetsPluginKey, {
			type: SNIPPETS_META_REMOVE_LOADER,
			id: loaderId,
		} satisfies SnippetsMeta),
	);
};

export const removeSnippetLoaderAndInsert = ({
	api,
	loaderId,
	loaderPosAtAdd,
	adf,
}: RemoveLoaderAndInsertParams): void => {
	api?.core?.actions.execute(({ tr }: { tr: Transaction }) => {
		tr.setMeta(snippetsPluginKey, {
			type: SNIPPETS_META_REMOVE_LOADER,
			id: loaderId,
		} satisfies SnippetsMeta);

		const currentState = api?.snippets?.sharedState.currentState();
		const loaderFromSharedState = currentState?.pending.find(
			(pending: PendingSnippetLoader) => pending.id === loaderId,
		);
		const insertPos = loaderFromSharedState?.pos ?? loaderPosAtAdd;

		if (insertPos !== undefined) {
			const fragment = Fragment.fromJSON(tr.doc.type.schema, adf);
			tr.insert(insertPos, fragment);
		}

		return tr;
	});
};
